import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Users, FileText, TrendingUp, ShieldAlert } from 'lucide-react';
import { PageHeader, Tabs, TabPanel, Empty, ErrorBlock, LoadingBlock, useLoad } from './kit';
import OversightTab from './reports/OversightTab';
import TeamTab from './reports/TeamTab';
import ReportsTab from './reports/ReportsTab';
import TimeCaseNotesReport from './reports/TimeCaseNotesReport';
import TrendsTab from './reports/TrendsTab';
import { API, PAGE_PERMS, HOURS_PERMS, LinkBtn, calendarPresets, hasAny } from './reports/shared';

// Reports and oversight (Support Coordination › Overview › "Reports & oversight", ?view=dashboard).
// For team leaders and the office: four questions, four tabs.
//   Oversight — who or what needs attention (default)     Team — how each coordinator is going
//   Reports   — give me the file (CSV / Excel)            Trends — a few honest charts
// The tab, the coordinator filter and the chosen report live in the address, so links land on the
// right view. Old links (?tab=dashboard|history|analytics) open the tab that replaced them; the old
// Case-note register (?report=notes-register) is now Time and Case Notes.
// Time and Case Notes pre-filtered to a participant (the participant Notes screen links here):
//   /support-coordination?view=dashboard&tab=reports&report=time-case-notes&client_ids=<client id>[&from=&to=]
// Someone with Log time but no oversight permission gets that one report on its own.
// Data: /api/sc/insights (backend/routes/scReports.js).

const LEGACY_TAB = { dashboard: 'team', history: 'reports', analytics: 'trends' };
const LEGACY_REPORT = { 'notes-register': 'time-case-notes' };
const TCN_PERMS = ['log_time', 'view_all_hours', 'claiming', 'reconciliation', 'generate_pay_files', 'admin'];

export default function CoordinationDashboard({ embedded = false }) {
  const [sp, setSp] = useSearchParams();
  const me = useLoad('/sc/me');
  const perms = useMemo(() => (me.data && me.data.permissions) || [], [me.data]);
  const canPage = hasAny(perms, PAGE_PERMS);
  const canTeam = hasAny(perms, HOURS_PERMS);

  const setParams = useCallback((patch) => setSp((p) => {
    const n = new URLSearchParams(p);
    Object.entries(patch).forEach(([k, v]) => { if (v == null || v === '') n.delete(k); else n.set(k, v); });
    return n;
  }, { replace: true }), [setSp]);

  const rawTab = sp.get('tab');
  const coordinator = sp.get('coordinator') || '';
  const rawReport = sp.get('report') || (rawTab === 'history' ? 'time-case-notes' : '');
  const report = LEGACY_REPORT[rawReport] || rawReport;
  const tcnClients = sp.get('client_ids') || '';
  const tcnFrom = sp.get('from') || '';
  const tcnTo = sp.get('to') || '';
  const tcn = useMemo(() => ({ clientIds: tcnClients, from: tcnFrom, to: tcnTo }), [tcnClients, tcnFrom, tcnTo]);

  // Oversight loads here so its tab can carry the count of items that need attention.
  const oversight = useLoad(`${API}/oversight`, coordinator ? { coordinator_id: coordinator } : null, { skip: !canPage });
  const ov = oversight.data;

  const tabs = useMemo(() => [
    { value: 'oversight', label: 'Oversight', icon: AlertTriangle, count: ov ? ov.total : 0, alert: !!(ov && ov.red) },
    canTeam && { value: 'team', label: 'Team', icon: Users },
    { value: 'reports', label: 'Reports', icon: FileText },
    { value: 'trends', label: 'Trends', icon: TrendingUp },
  ].filter(Boolean), [ov, canTeam]);
  const wanted = LEGACY_TAB[rawTab] || rawTab || (report ? 'reports' : '');
  const tab = tabs.some((t) => t.value === wanted) ? wanted : 'oversight';
  const setTab = (v) => setParams({ tab: v === 'oversight' ? null : v, report: v === 'reports' ? report || null : null,
    ...(v === 'reports' ? {} : { client_ids: null, from: null, to: null }) });

  // Periods are kept while you move between tabs.
  const [teamPeriod, setTeamPeriod] = useState({ key: 'pay-current', from: null, to: null });
  const [reportPeriod, setReportPeriod] = useState(() => { const p = calendarPresets()[0]; return { key: p.key, from: p.from, to: p.to }; });
  const [months, setMonths] = useState(6);

  const header = embedded ? null : (
    <PageHeader title="Reports and oversight" sub="What needs attention across the caseload, how each coordinator is going, standard reports and trends." />
  );

  if (!me.data) {
    return <>{header}{me.error ? <ErrorBlock error={me.error} onRetry={me.reload} /> : <LoadingBlock rows={5} />}</>;
  }
  if (!canPage && report === 'time-case-notes' && hasAny(perms, TCN_PERMS)) {
    return (
      <>
        {embedded ? null : <PageHeader title="Time and Case Notes" sub="Your time entries with their case notes, in Excel or PDF." />}
        <TimeCaseNotesReport initialClientIds={tcn.clientIds} initialFrom={tcn.from} initialTo={tcn.to} header={<span id="tcn-h" className="sr-only">Time and Case Notes</span>} />
      </>
    );
  }
  if (!canPage) {
    return (
      <>
        {header}
        <div className="bg-white rounded-2xl border border-slate-200/80">
          <Empty icon={ShieldAlert} title="This page is for team leaders and the office"
            action={<LinkBtn to="/support-coordination" variant="secondary" size="md">Back to Overview</LinkBtn>}>
            You need the View all hours or Claiming permission to see the team’s oversight and reports. An admin can add it in Settings.
          </Empty>
        </div>
      </>
    );
  }

  return (
    <div>
      {header}
      <Tabs value={tab} onChange={setTab} tabs={tabs} label="View">
        <TabPanel value="oversight">
          <OversightTab load={oversight} coordinator={coordinator} onCoordinator={(v) => setParams({ coordinator: v || null })} />
        </TabPanel>
        {canTeam && (
          <TabPanel value="team">
            <TeamTab period={teamPeriod} onPeriod={setTeamPeriod} />
          </TabPanel>
        )}
        <TabPanel value="reports">
          <ReportsTab selected={report} onSelect={(k) => setParams({ report: k, ...(k === 'time-case-notes' ? {} : { client_ids: null, from: null, to: null }) })}
            period={reportPeriod} onPeriod={setReportPeriod} tcn={tcn} />
        </TabPanel>
        <TabPanel value="trends">
          <TrendsTab months={months} onMonths={setMonths} coordinator={coordinator} onCoordinator={(v) => setParams({ coordinator: v || null })} />
        </TabPanel>
      </Tabs>
    </div>
  );
}
